import React, { useEffect } from 'react';
import Container from '@material-ui/core/Container';
import { useNavigate, useParams } from 'react-router-dom';
import { FormPage } from '../../../components/FormPage';
import { FormSection } from '../../../components/FormSection';
import Typography from '@material-ui/core/Typography/Typography';
import {
    SubmitPayoutRequestVariables,
    useGetDeveloperPayout,
    useInvoiceFileDelete,
    useS3PayoutUploadMutation,
    useUpdatePayoutRequest,
} from '../../../types/generated';
import { FullscreenLoader } from '../../../components/FullscreenLoader/FullscreenLoader';
import { Field } from 'react-final-form';
import { PdfUploadInput } from '../../../components/PdfUploadInput';
import * as Yup from 'yup';
import { useOrganizationStatus } from '../../../queries/useOrganizationStatus';
import { InvalidTaxForm } from './components/InvalidTaxForm';
import { InvalidPayoutSettings } from './components/InvalidPayoutSettings';
import { Pre } from './components/StyledPre';
import AlertTitle from '@material-ui/lab/AlertTitle';
import Alert from '@material-ui/lab/Alert';

const validationSchema = Yup.object().shape({
    invoice: Yup.object().required('You have to upload an invoice').nullable(),
});

export const PayoutRequestEdit = (): JSX.Element => {
    const { id } = useParams<{ id: string }>();
    const { loading, payoutSettingsPreview, hasValidTaxForm, hasValidPayoutSettings, refetch } =
        useOrganizationStatus();
    const { data: invoiceData, loading: loadingInvoice } = useGetDeveloperPayout({
        variables: { id },
    });
    const navigate = useNavigate();
    const [upload] = useS3PayoutUploadMutation();
    const [updatePayoutRequest, { loading: loadingUpdate }] = useUpdatePayoutRequest();
    const [deleteInvoice, { loading: deletingInvoice }] = useInvoiceFileDelete();

    useEffect(() => {
        refetch();
    }, [refetch]);

    const redirectBack = () => {
        navigate('/extranet/payouts');
    };

    const submit = (values): void => {
        const data: SubmitPayoutRequestVariables = {
            fileInput: {
                ...values.invoice,
                temp: false,
            },
        };

        updatePayoutRequest({
            variables: {
                id: id,
                ...data,
            },
        }).then(() => {
            redirectBack();
        });
    };

    const getUploadParams = async ({ file, meta }) => {
        const { data: uploadData } = await upload();
        return {
            body: file,
            method: 'PUT',
            url: uploadData.s3UploadPayoutInvoice.node.uploadUrl,
            meta: { ...meta, ...uploadData.s3UploadPayoutInvoice.node.meta },
        };
    };

    if (loading) {
        return <FullscreenLoader />;
    }

    if (!hasValidTaxForm) {
        return <InvalidTaxForm />;
    }

    if (!hasValidPayoutSettings) {
        return <InvalidPayoutSettings />;
    }

    if (invoiceData?.getPayoutDetails?.status !== 'REJECTED') {
        return (
            <Alert severity={'error'}>
                <Typography>
                    This payout request is not rejected, you cannot modify this request.
                </Typography>
            </Alert>
        );
    }

    return (
        <Container maxWidth={'md'}>
            {invoiceData.getPayoutDetails.status === 'REJECTED' && (
                <Alert severity={'error'}>
                    <AlertTitle>Payout request rejected</AlertTitle>
                    <Typography>
                        <strong>Reason: </strong>
                        {invoiceData.getPayoutDetails.reason}
                    </Typography>
                </Alert>
            )}
            <FormPage
                title={'Payout request'}
                initialValues={{ invoice: invoiceData.getPayoutDetails.payoutInvoice }}
                validationSchema={validationSchema}
                onSubmit={submit}
                loading={loading || loadingInvoice || loadingUpdate}
                render={() => (
                    <React.Fragment>
                        <FormSection
                            title={'PDF Invoice'}
                            description={'Upload your PDF invoice for this payout request'}
                        >
                            <Field
                                name="invoice"
                                label="Upload invoice"
                                component={PdfUploadInput}
                                getUploadParams={getUploadParams}
                                multiple={false}
                                maxFiles={1}
                                onDelete={(s3Key, callback) => {
                                    deleteInvoice({
                                        variables: {
                                            fileKey: s3Key,
                                        },
                                    });
                                    callback();
                                }}
                                deleting={deletingInvoice}
                            />
                        </FormSection>
                        <FormSection
                            title={'Tax form and payout settings'}
                            description={
                                'A preview of your tax form and payout settings to be submitted with this request, make sure the information is correct and up to date.'
                            }
                        >
                            <Typography>
                                <strong>Preview:</strong>
                            </Typography>
                            <Pre
                                dangerouslySetInnerHTML={{
                                    __html: payoutSettingsPreview,
                                }}
                            />
                        </FormSection>
                    </React.Fragment>
                )}
            />
        </Container>
    );
};
